<template>

    <div v-if="isOpen" class="absolute inset-0 z-20 flex justify-center items-center">
        <div class="relative max-w-md w-full p-8 rounded bg-white shadow-2xl border border-gray-200 pointer-events-auto" role="dialog">
            <button class="absolute p-4 top-0 right-0 text-muted outline-none cursor-pointer focus:outline-none" role="button" @click="handleCancel()">
                <icon name="x"/>
            </button>

            <div v-if="slot.initialized">
                <div class="text-2xl font-bold" v-text="resource.title"></div>
                <div v-if="project" v-text="project.title" class="mt-2 text-sm text-muted"></div>

                <ReservationDatetime
                    :readonly="isReadonlyModal"
                    @input="vacancyCheck()"/>

                <div class="grid gap-4">

                    <div>
                        <div class="px-4 py-3 rounded-sm bg-gray-200">
                            <div class="text-xs text-muted uppercase">Zadavatel</div>
                            <a class="hover:underline" :href="filterByOwnerUri(slot.owner.id)">
                                {{ slot.owner.fullName }}
                            </a>
                        </div>

                        <ReservationGuests
                            v-if="permissionToAddGuests()"
                            class="-mt-4"
                            :resource-accepts-guests="resource.accepts_guests"
                            :readonly="isReadonlyModal"/>
                    </div>

                    <ReservationCapacity
                        :readonly="isReadonlyModal"
                        @input="vacancyCheck()"/>

                    <ReservationRecurrence
                        v-model="withFollowing"
                        :max-recurrence-count="maxRecurrenceCount"
                        @input="vacancyCheck()"/>

                    <div class="font-bold text-error" v-if="! isVacant">
                        {{ isVacantMessage || 'Zvolený rozsah není dostupný' }}
                    </div>

                    <ReservationStatus
                        v-if="slot.exists && slot.requiresApproval"
                        :withFollowing="withFollowing"
                        @status-changed="closeModal()"/>
                </div>

                <div class="mt-8 flex">
                    <btn class="btn--primary mr-1"
                        v-if="isCreateModal"
                        @click.native="handleStore()"
                        :loading="saving"
                    >Rezervovat</btn>
                    <btn class="btn--primary mr-1"
                        v-if="slot.permissions.update"
                        @click.native="handleUpdate()"
                        :loading="saving"
                    >Upravit</btn>
                    <button
                        class="btn"
                        @click="handleCancel()"
                    >Zavřít</button>
                    <btn v-if="slot.permissions.delete"
                        class="btn ml-auto bg-error border-error text-white"
                        @click.native="handleDelete()"
                        :loading="deleting"
                    ><icon name="trash-2"/></btn>
                </div>

            </div>

        </div>
    </div>

</template>

<script>
import ReservationDatetime from './ReservationDatetime';
import ReservationGuests from './ReservationGuests';
import ReservationCapacity from './ReservationCapacity';
import ReservationRecurrence from './ReservationRecurrence';
import ReservationStatus from './ReservationStatus';
import { throttle } from 'lodash';
import Slot from '@/components/Slot';
import axios from 'axios';

export default {
    components: {
        ReservationDatetime,
        ReservationGuests,
        ReservationCapacity,
        ReservationRecurrence,
        ReservationStatus,
    },

    props: {
        selection: null,
        projectId: null,
        resourceId: null,
        slotId: null,
    },

    computed: {
        isReadonlyModal() {
            return this.slot.exists
                && ! this.slot.permissions.update;
        },
        isCreateModal() {
            return ! this.slot.exists;
        }
    },

    data() {
        return {
            resource: null,
            project: null,
            maxRecurrenceCount: 10,
            slot: this.$store.slot,
            isOpen: false,
            isVacant: true,
            isVacantMessage: null,
            withFollowing: false,
            saving: false,
            deleting: false,
        }
    },

    methods: {

        filterByOwnerUri(ownerId) {
            return window.Uri()
                .addQuery('filter[owner_id]', ownerId)
                .toString();
        },

        async fetchResource(resourceId) {
            let response = await axios.get(`/api/v1/resources/${resourceId}`)
            this.resource = response.data.data;
        },

        async fetchProject(projectId) {
            let response = await axios.get(`/api/v1/projects/${projectId}`)
            this.project = response.data.data;
        },

        async fetchSlot() {
            this.resetSlot();
            await this.slot.fetch(this.slotId);
            this.resource = this.slot.resource;
            this.project = this.slot.project;
            this.isOpen = true;
        },

        createSlot() {
            this.$store.slot = new Slot({
                resource: this.resource,
                project: this.project,
                start: this.selection.start,
                end: this.selection.end,
                capacity: 1,
            });

            if (this.project) {
                this.$store.slot.owner = this.project.owner;
            }

            this.slot = this.$store.slot;
            this.isOpen = true;
        },

        vacancyCheck: throttle(function () {
            let endpoint = `/resources/${this.resource.id}/check-is-vacant`;

            let params = this.slot.toJSON();
            params.withFollowing = this.withFollowing;
            params.ignore = this.slot.id;
            params.recurrenceCount = parseInt(this.slot.recurrences.count);

            axios
                .get(endpoint, { params })
                .then(response => {
                    this.isVacant = response.data.isVacant;
                    if (this.isVacant) {
                        this.isVacantMessage = null;
                    } else {
                        this.isVacantMessage = response.data.message;
                    }
                })
                .catch(error => {
                    if (error.response.status !== 422) {
                        console.error(error);
                        alert('vacancy check error')
                    }

                    this.isVacant = false;
                });

        }, 500),

        handleEdit() {
            this.slotId && this.fetchSlot();
            this.withFollowing = false;
        },

        async handleStore() {
            // if not vacant, display error
            if (! this.isVacant) {
                Notify.swal({
                    icon: 'error',
                    title: 'Zvolený rozsah není dostupný'
                });
                return false;
            }

            this.saving = true;
            await this.slot.store();
            this.saving = false;
            this.refreshCalendar();
            this.closeModal();
        },

        async handleUpdate() {
            // if not vacant, display error
            if (! this.isVacant) {
                Notify.swal({
                    icon: 'error',
                    title: 'Zvolený rozsah není dostupný'
                });
                return false;
            }

            this.saving = true;
            await this.slot.update(this.withFollowing);
            this.saving = false;
            this.refreshCalendar();
            this.closeModal();
        },

        async handleDelete() {
            if (! this.slot.exists) {
                this.closeModal();
                return;
            }

            let resolved = await window.Swal.fire({
                title: 'Opravdu smazat?',
                // text: confirmationPromptText,
                icon: 'warning',
                showCancelButton: true
            });

            if (! resolved.isConfirmed) {
                return;
            }

            this.deleting = true;
            await this.slot.delete(this.withFollowing);
            this.deleting = false;
            this.refreshCalendar();

            this.closeModal();
        },

        handleCancel() {
            this.closeModal();
        },

        refreshCalendar() {
            window.events.$emit('refreshCalendar');
        },

        closeModal() {
            this.$emit('close');
            this.isOpen = false;
            this.resetSlot();
            this.editingStatus = false;
        },

        resetSlot() {
            this.$store.slot = new Slot;
            this.slot = this.$store.slot;
        },

        permissionToAddGuests() {
            return window.App.auth && window.App.auth.roles.indexOf('stud') === -1; // not student
        }
    },

    watch: {
        slotId() {
            this.handleEdit();
        },
        withFollowing() {
            this.slot.initialized && this.vacancyCheck();
        },
        selection() {
            this.working = false;
            return this.selection
                ? this.createSlot()
                : this.slot.reset();
        },
        slot: {
            handler() {
                if (! this.isOpen) {
                    return false;
                }
                if (this.slot.recurrences.count > this.maxRecurrenceCount) {
                    this.slot.recurrences.count = this.maxRecurrenceCount;
                }
                this.vacancyCheck();
            },
            deep: true,
        }
    },

    mounted() {
        this.resourceId && this.fetchResource(this.resourceId);
        this.projectId && this.fetchProject(this.projectId);
    }
}
</script>
